import { getMainPaywall } from "@circle-react/components/Paywalls/CheckoutWithSession/helpers/SessionStepQueryHelper";
import { useConversionTrackingContext } from "@circle-react/contexts/ConversionTrackingContext";
import { cannotPurchaseAgain } from "@circle-react/helpers/paywallCheckoutHelpers";
import { useEffect } from "react";

import { ConversionTrackingEvent } from "@/react/api/conversionTrackingApi";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";

import {
  getConversionTrackingItems,
  getConversionTrackingPriceValue,
} from "../../helpers/conversionTrackingPriceHelpers";
import {
  getPaywallPrice,
  getQueryPaywallPriceId,
} from "../../helpers/paywallPrices";
interface UseBeginCheckoutTrackingParams {
  funnelUuid: string;
  paywallId: number;
  session: CheckoutSession | null | undefined;
  wasSessionFetched: boolean;
}

// Fires `begin_checkout` once per funnel run, only on a fresh
// (non-redirect-return) mount -- a redirect return (3DS/SEPA/iDEAL/Bancontact)
// means both funnel events already fired pre-redirect.
//
// Fires regardless of whether the member is already authenticated at
// checkout start: a prior "skip if already a member" condition broke
// retargeting/upsell campaigns aimed at existing members, making them look
// like they had zero funnel activity.
//
// Tracking readiness is handled by `ConversionTrackingContext`; this hook
// emits once per session and never reruns just because consent/capture changed.
//
export const useBeginCheckoutTracking = ({
  session,
  wasSessionFetched,
  funnelUuid,
  paywallId,
}: UseBeginCheckoutTrackingParams) => {
  const { track } = useConversionTrackingContext();

  useEffect(() => {
    if (!session || wasSessionFetched) return;

    const mainPaywall = getMainPaywall(session);

    // PAY-404 -- skip `begin_checkout` when the member cannot repurchase this
    // paywall: firing a top-of-funnel event here would pollute
    // funnel-conversion-rate metrics with a checkout that was never going to
    // convert (an access-control dead end, not a "did the campaign work"
    // signal). Sourced from the session's main paywall since
    // `usePaywallCheckoutContext()` isn't available yet at this point.
    if (
      cannotPurchaseAgain(
        !!mainPaywall?.policies.has_already_purchased,
        mainPaywall,
      )
    ) {
      return;
    }

    // PAY-404 -- `value`/`currency`/`items` are read from the session's main
    // offer paywall, not `usePaywallCheckoutContext().getSelectedPrice()`:
    // that context mounts deeper in the tree, under `CheckoutPage`, which
    // doesn't exist yet when this effect fires (right after session
    // creation). `value` here is therefore the price's bare nominal amount,
    // NOT the trial/coupon-adjusted amount actually due right now -- a known,
    // structural asymmetry with `add_payment_info` (which reports the
    // accurate due-now amount), not an inconsistency to fix here.
    const selectedPrice = mainPaywall
      ? getPaywallPrice(mainPaywall, getQueryPaywallPriceId())
      : undefined;

    track({
      eventName: ConversionTrackingEvent.BEGIN_CHECKOUT,
      funnelUuid,
      checkoutSessionId: session.id,
      paywallId,
      paywallPriceId: selectedPrice?.id,
      value: selectedPrice && getConversionTrackingPriceValue(selectedPrice),
      currency: mainPaywall?.currency.code,
      currencySmallestUnit: mainPaywall?.currency.smallest_unit,
      items: selectedPrice && getConversionTrackingItems(selectedPrice),
    });
  }, [session?.id]);
};
